'use client'

import type { ReactNode } from 'react'
import * as AriakitMenu from '@ariakit/react/menu'
import { ComboboxItem, useComboboxContext } from '@ariakit/react'
import { CheckIcon, MinusIcon } from 'lucide-react'
import Button from '@shared/components/Button/Button'
import * as styles from './Menu.css'
import { MenuItemContent, type MenuItemSlots } from './MenuItem'
import { SEARCH_CONTEXT_ATTRIBUTE } from './MenuSearch'

interface SelectionItemProps extends MenuItemSlots {
  /** `mixed`: some children of a tree node are selected. */
  checked: boolean | 'mixed'
  onCheckedChange: (checked: boolean) => void
  /** Checkbox items keep the menu open by default, radio items close it. */
  hideOnClick?: boolean
  disabled?: boolean
  /**
   * In a searchable menu, a row shown only as context for matching rows
   * below it, e.g. a tree node above a match. The search never auto-selects
   * it, so Enter picks the match.
   */
  searchContext?: boolean
  className?: string
  children: ReactNode
}

function Control({
  kind,
  checked,
}: {
  kind: 'checkbox' | 'radio'
  checked: boolean | 'mixed'
}) {
  return (
    <span
      className={`${styles.check}${kind === 'radio' ? ` ${styles.radio}` : ''}`}
      data-checked={checked === false ? undefined : ''}
      aria-hidden
    >
      {kind === 'checkbox' && checked === true && (
        <CheckIcon size={12} strokeWidth={3} />
      )}
      {kind === 'checkbox' && checked === 'mixed' && (
        <MinusIcon size={12} strokeWidth={3} />
      )}
    </span>
  )
}

/**
 * One row of a selection list. Inside a searchable menu (an Ariakit combobox
 * wraps the menu) the row is a combobox option, so arrow keys keep working
 * while the search field holds focus; otherwise a menu item with the
 * checkbox or radio role. Either way `aria-checked` carries a tree node's
 * mixed state, which `aria-selected` cannot.
 */
function SelectionItem({
  kind,
  checked,
  onCheckedChange,
  hideOnClick,
  disabled,
  searchContext = false,
  className = '',
  children,
  ...slots
}: SelectionItemProps & { kind: 'checkbox' | 'radio' }) {
  const searchable = useComboboxContext() !== undefined
  const content = (
    <MenuItemContent
      lead={<Control kind={kind} checked={checked} />}
      {...slots}
    >
      {children}
    </MenuItemContent>
  )
  const render = (
    <Button variant='flat' className={`${styles.item} ${className}`} />
  )
  const onClick = () => onCheckedChange(checked !== true)

  if (searchable) {
    return (
      <ComboboxItem
        render={render}
        aria-selected={checked === true}
        aria-checked={checked}
        focusOnHover
        setValueOnClick={false}
        selectValueOnClick={false}
        hideOnClick={hideOnClick}
        disabled={disabled}
        onClick={onClick}
        {...(searchContext && { [SEARCH_CONTEXT_ATTRIBUTE]: '' })}
      >
        {content}
      </ComboboxItem>
    )
  }
  return (
    <AriakitMenu.MenuItem
      render={render}
      role={kind === 'checkbox' ? 'menuitemcheckbox' : 'menuitemradio'}
      aria-checked={checked}
      hideOnClick={hideOnClick}
      disabled={disabled}
      onClick={onClick}
    >
      {content}
    </AriakitMenu.MenuItem>
  )
}

export function MenuItemCheckbox({
  hideOnClick = false,
  ...props
}: SelectionItemProps) {
  return <SelectionItem kind='checkbox' hideOnClick={hideOnClick} {...props} />
}

export function MenuItemRadio({
  hideOnClick = true,
  ...props
}: SelectionItemProps) {
  return <SelectionItem kind='radio' hideOnClick={hideOnClick} {...props} />
}
